import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  InputBase,
  Typography,
  Switch,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import { useNavigate, useParams } from "react-router-dom";
import CustomAxios from "../../utils/CustomAxios";
import { useSelector } from "react-redux";
import { useGetInventory } from "../globalFunctions";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export function AddSupplier() {
  const toast = useRef(null);
  const navigate = useNavigate();
  const { id } = useParams(); // Get supplier ID from URL (undefined for create mode)
  const user_id = localStorage.getItem('user_id');
  const inventory = useSelector(state => state.inventory.inventory);
  const getInventory = useGetInventory();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(!!id);
  const isEditMode = !!id;
  const user_role = localStorage.getItem("role");
  const isMerchant = user_role === 'merchant';
  const hubId = localStorage.getItem("selected_hub");

  const [supplierForm, setSupplierForm] = useState({
    name: "",
    supplier_code: "",
    description: "",
    contact_person: "",
    contact_email: "",
    contact_phone: "",
    alternate_phone: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    country: "India",
    gst_number: "",
    pan_number: "",
    registration_number: "",
    supplier_type: "wholesale",
    lead_time_days: "",
    delivery_terms: "",
    is_active: true,
    inventory: isMerchant && hubId ? parseInt(hubId) : "",
  });

  const [supplierErrors, setSupplierErrors] = useState({});

  const supplierTypeOptions = [
    { name: "Wholesale Supplier", value: "wholesale" },
    { name: "Distributor", value: "distributor" },
    { name: "Manufacturer", value: "manufacturer" },
    { name: "Other", value: "other" },
  ];

  useEffect(() => {
    getInventory();
  }, []);

  // Fetch supplier data in edit mode
  useEffect(() => {
    const fetchSupplierData = async () => {
      if (!id) return;

      try {
        setFetching(true);
        const response = await CustomAxios.get(`suppliers/${id}/`);
        const supplier = response.data;

        setSupplierForm({
          name: supplier.name || "",
          supplier_code: supplier.supplier_code || "",
          description: supplier.description || "",
          contact_person: supplier.contact_person || "",
          contact_email: supplier.contact_email || "",
          contact_phone: supplier.contact_phone || "",
          alternate_phone: supplier.alternate_phone || "",
          address: supplier.address || "",
          city: supplier.city || "",
          state: supplier.state || "",
          pincode: supplier.pincode || "",
          country: supplier.country || "India",
          gst_number: supplier.gst_number || "",
          pan_number: supplier.pan_number || "",
          registration_number: supplier.registration_number || "",
          supplier_type: supplier.supplier_type || "wholesale",
          lead_time_days: supplier.lead_time_days || "",
          delivery_terms: supplier.delivery_terms || "",
          is_active: supplier.is_active !== undefined ? supplier.is_active : true,
          inventory: supplier.inventory || (isMerchant && hubId ? parseInt(hubId) : ""),
        });
      } catch (error) {
        console.error("Error fetching supplier:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch supplier details",
          life: 3000,
        });
        navigate("/home/suppliers");
      } finally {
        setFetching(false);
      }
    };

    fetchSupplierData();
  }, [id, navigate, isMerchant, hubId]);

  const validateSupplierForm = () => {
    const errors = {};

    if (!supplierForm.name || supplierForm.name.trim() === "") {
      errors.name = "Supplier name is required";
    }
    if (!supplierForm.contact_person || supplierForm.contact_person.trim() === "") {
      errors.contact_person = "Contact person is required";
    }
    if (!supplierForm.contact_email || supplierForm.contact_email.trim() === "") {
      errors.contact_email = "Contact email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(supplierForm.contact_email)) {
      errors.contact_email = "Invalid email format";
    }
    if (!supplierForm.contact_phone || supplierForm.contact_phone.trim() === "") {
      errors.contact_phone = "Contact phone is required";
    }
    if (!supplierForm.address || supplierForm.address.trim() === "") {
      errors.address = "Address is required";
    }
    if (!supplierForm.city || supplierForm.city.trim() === "") {
      errors.city = "City is required";
    }
    if (!supplierForm.state || supplierForm.state.trim() === "") {
      errors.state = "State is required";
    }
    if (!supplierForm.pincode || supplierForm.pincode.trim() === "") {
      errors.pincode = "Pincode is required";
    }

    // Validate inventory for non-merchants
    if (!isMerchant && (!supplierForm.inventory || supplierForm.inventory === "")) {
      errors.inventory = "Inventory is required";
    }

    // Validate PAN if provided
    if (supplierForm.pan_number && supplierForm.pan_number.trim() !== "") {
      if (supplierForm.pan_number.length !== 10) {
        errors.pan_number = "PAN number must be exactly 10 characters";
      } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(supplierForm.pan_number.toUpperCase())) {
        errors.pan_number = "Invalid PAN number format";
      }
    }

    // Validate GST if provided
    if (supplierForm.gst_number && supplierForm.gst_number.trim() !== "") {
      if (supplierForm.gst_number.length !== 15) {
        errors.gst_number = "GST number must be exactly 15 characters";
      }
    }

    // Validate lead_time_days if provided
    if (supplierForm.lead_time_days && supplierForm.lead_time_days !== "") {
      const leadTime = Number(supplierForm.lead_time_days);
      if (isNaN(leadTime) || leadTime < 0) {
        errors.lead_time_days = "Lead time must be a positive number";
      }
    }

    setSupplierErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSupplierInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === "lead_time_days") {
      newValue = value === "" ? "" : Number(value);
    }

    setSupplierForm((prev) => ({
      ...prev,
      [name]: newValue,
    }));
  };

  const handleSupplierToggleChange = (name) => (event) => {
    const checked = event.target.checked;
    setSupplierForm((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSaveSupplier = async () => {
    if (!validateSupplierForm()) {
      return;
    }

    const payload = {
      name: supplierForm.name.trim(),
      supplier_code: supplierForm.supplier_code?.trim() || null,
      description: supplierForm.description?.trim() || null,
      contact_person: supplierForm.contact_person.trim(),
      contact_email: supplierForm.contact_email.trim(),
      contact_phone: supplierForm.contact_phone.trim(),
      alternate_phone: supplierForm.alternate_phone?.trim() || null,
      address: supplierForm.address.trim(),
      city: supplierForm.city.trim(),
      state: supplierForm.state.trim(),
      pincode: supplierForm.pincode.trim(),
      country: supplierForm.country || "India",
      gst_number: supplierForm.gst_number?.trim() || null,
      pan_number: supplierForm.pan_number?.trim() || null,
      registration_number: supplierForm.registration_number?.trim() || null,
      supplier_type: supplierForm.supplier_type,
      lead_time_days: supplierForm.lead_time_days ? Number(supplierForm.lead_time_days) : null,
      delivery_terms: supplierForm.delivery_terms?.trim() || null,
      is_active: !!supplierForm.is_active,
      inventory: supplierForm.inventory || null,
      created_by: isEditMode ? null : user_id,
      updated_by: isEditMode ? user_id : null,
    };

    try {
      setLoading(true);
      if (isEditMode) {
        await CustomAxios.put(`suppliers/${id}/`, payload);
        toast.current?.show({
          severity: "success",
          summary: "Updated",
          detail: "Supplier updated successfully",
          life: 3000,
        });
      } else {
        await CustomAxios.post("suppliers/", payload);
        toast.current?.show({
          severity: "success",
          summary: "Created",
          detail: "Supplier created successfully",
          life: 3000,
        });
      }
      navigate("/home/suppliers");
    } catch (error) {
      console.error("Error saving supplier:", error);
      
      let errorMessage = "Failed to save supplier";
      if (error?.response?.data) {
        const errorData = error.response.data;
        if (errorData.details) {
          if (typeof errorData.details === 'string') {
            errorMessage = errorData.details;
          } else if (typeof errorData.details === 'object') {
            const firstError = Object.values(errorData.details)[0];
            errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
          }
        } else if (errorData.error) {
          errorMessage = typeof errorData.error === 'string' ? errorData.error : JSON.stringify(errorData.error);
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }
      
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: errorMessage,
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isMerchant) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          padding: "20px",
        }}
      >
        <Typography variant="h6" color="error">
          Access Denied
        </Typography>
        <Typography>
          You don't have permission to manage suppliers.
        </Typography>
      </Box>
    );
  }

  if (fetching) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        width: "100%",
        padding: "30px",
      }}
    >
      <Toast ref={toast} />
      
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <IconButton onClick={() => navigate("/home/suppliers")}>
          <ArrowBackIcon />
        </IconButton>
        <Typography
          sx={{ fontSize: "20px", fontWeight: "700", lineHeight: "28px" }}
        >
          {isEditMode ? "Edit Supplier" : "Add Supplier"}
        </Typography>
      </Box>

      {/* Form Content */}
      <Box
        sx={{
          backgroundColor: "white",
          padding: "24px",
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
        }}
      >
        {/* Basic Information */}
        <Typography sx={{ fontSize: "18px", fontWeight: "600" }}>
          Basic Information
        </Typography>

        {!isMerchant && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}
            >
              Inventory<span style={{ color: "red", marginLeft: 4 }}>*</span>
            </Typography>
            <Dropdown
              value={supplierForm.inventory}
              options={[
                { name: "Select Inventory", value: "" },
                ...(inventory || []).map((inv) => ({
                  name: inv.name || inv.place,
                  value: inv.value || inv.id,
                })),
              ]}
              optionLabel="name"
              optionValue="value"
              placeholder="Select Inventory"
              className="w-full md:w-20rem"
              appendTo="self"
              onChange={(e) =>
                setSupplierForm((prev) => ({
                  ...prev,
                  inventory: e.value,
                }))
              }
            />
            {supplierErrors.inventory && (
              <Typography
                sx={{ color: "red", fontSize: "12px", lineHeight: "18px" }}
              >
                {supplierErrors.inventory}
              </Typography>
            )}
          </Box>
        )}
        
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <Typography
            sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}
          >
            Supplier Name<span style={{ color: "red", marginLeft: 4 }}>*</span>
          </Typography>
          <InputBase
            name="name"
            value={supplierForm.name}
            onChange={handleSupplierInputChange}
            placeholder="Enter supplier name"
            sx={{
              backgroundColor: "white",
              width: "100%",
              height: "40px",
              borderRadius: "4px",
              border: "1px solid",
              borderColor: supplierErrors.name ? "red" : "secondary.border",
              padding: "8px 12px",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "22px",
              color: "black",
            }}
          />
          {supplierErrors.name && (
            <Typography
              sx={{ color: "red", fontSize: "12px", lineHeight: "18px" }}
            >
              {supplierErrors.name}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: "16px" }}>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}
            >
              Supplier Code
            </Typography>
            <InputBase
              name="supplier_code"
              value={supplierForm.supplier_code}
              onChange={handleSupplierInputChange}
              placeholder="Auto-generated if empty"
              disabled={isEditMode}
              sx={{
                backgroundColor: "white",
                width: "100%",
                height: "40px",
                borderRadius: "4px",
                border: "1px solid",
                borderColor: "secondary.border",
                padding: "8px 12px",
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "22px",
                color: "black",
              }}
            />
          </Box>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}
            >
              Supplier Type<span style={{ color: "red", marginLeft: 4 }}>*</span>
            </Typography>
            <Dropdown
              value={supplierForm.supplier_type}
              options={supplierTypeOptions}
              name="supplier_type"
              optionLabel="name"
              optionValue="value"
              placeholder="Select supplier type"
              className="w-full md:w-20rem"
              appendTo="self"
              onChange={(e) =>
                setSupplierForm((prev) => ({
                  ...prev,
                  supplier_type: e.value,
                }))
              }
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <Typography
            sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}
          >
            Description
          </Typography>
          <InputBase
            name="description"
            value={supplierForm.description}
            onChange={handleSupplierInputChange}
            placeholder="Enter description"
            multiline
            rows={3}
            sx={{
              backgroundColor: "white",
              width: "100%",
              minHeight: "80px",
              borderRadius: "4px",
              border: "1px solid",
              borderColor: "secondary.border",
              padding: "8px 12px",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "22px",
              color: "black",
            }}
          />
        </Box>

        {/* Contact Information */}
        <Typography sx={{ fontSize: "18px", fontWeight: "600", mt: 2 }}>
          Contact Information
        </Typography>

        <Box sx={{ display: "flex", gap: "16px" }}>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}
            >
              Contact Person<span style={{ color: "red", marginLeft: 4 }}>*</span>
            </Typography>
            <InputBase
              name="contact_person"
              value={supplierForm.contact_person}
              onChange={handleSupplierInputChange}
              placeholder="Enter contact person name"
              sx={{
                backgroundColor: "white",
                width: "100%",
                height: "40px",
                borderRadius: "4px",
                border: "1px solid",
                borderColor: supplierErrors.contact_person ? "red" : "secondary.border",
                padding: "8px 12px",
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "22px",
                color: "black",
              }}
            />
            {supplierErrors.contact_person && (
              <Typography
                sx={{ color: "red", fontSize: "12px", lineHeight: "18px" }}
              >
                {supplierErrors.contact_person}
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}
            >
              Contact Email<span style={{ color: "red", marginLeft: 4 }}>*</span>
            </Typography>
            <InputBase
              name="contact_email"
              type="email"
              value={supplierForm.contact_email}
              onChange={handleSupplierInputChange}
              placeholder="Enter email"
              sx={{
                backgroundColor: "white",
                width: "100%",
                height: "40px",
                borderRadius: "4px",
                border: "1px solid",
                borderColor: supplierErrors.contact_email ? "red" : "secondary.border",
                padding: "8px 12px",
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "22px",
                color: "black",
              }}
            />
            {supplierErrors.contact_email && (
              <Typography
                sx={{ color: "red", fontSize: "12px", lineHeight: "18px" }}
              >
                {supplierErrors.contact_email}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: "16px" }}>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}
            >
              Contact Phone<span style={{ color: "red", marginLeft: 4 }}>*</span>
            </Typography>
            <InputBase
              name="contact_phone"
              value={supplierForm.contact_phone}
              onChange={handleSupplierInputChange}
              placeholder="Enter phone number"
              sx={{
                backgroundColor: "white",
                width: "100%",
                height: "40px",
                borderRadius: "4px",
                border: "1px solid",
                borderColor: supplierErrors.contact_phone ? "red" : "secondary.border",
                padding: "8px 12px",
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "22px",
                color: "black",
              }}
            />
            {supplierErrors.contact_phone && (
              <Typography
                sx={{ color: "red", fontSize: "12px", lineHeight: "18px" }}
              >
                {supplierErrors.contact_phone}
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}
            >
              Alternate Phone
            </Typography>
            <InputBase
              name="alternate_phone"
              value={supplierForm.alternate_phone}
              onChange={handleSupplierInputChange}
              placeholder="Enter alternate phone"
              sx={{
                backgroundColor: "white",
                width: "100%",
                height: "40px",
                borderRadius: "4px",
                border: "1px solid",
                borderColor: "secondary.border",
                padding: "8px 12px",
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "22px",
                color: "black",
              }}
            />
          </Box>
        </Box>

        {/* Address Information */}
        <Typography sx={{ fontSize: "18px", fontWeight: "600", mt: 2 }}>
          Address Information
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <Typography
            sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}
          >
            Address<span style={{ color: "red", marginLeft: 4 }}>*</span>
          </Typography>
          <InputBase
            name="address"
            value={supplierForm.address}
            onChange={handleSupplierInputChange}
            placeholder="Enter address"
            multiline
            rows={2}
            sx={{
              backgroundColor: "white",
              width: "100%",
              minHeight: "60px",
              borderRadius: "4px",
              border: "1px solid",
              borderColor: supplierErrors.address ? "red" : "secondary.border",
              padding: "8px 12px",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "22px",
              color: "black",
            }}
          />
          {supplierErrors.address && (
            <Typography
              sx={{ color: "red", fontSize: "12px", lineHeight: "18px" }}
            >
              {supplierErrors.address}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: "16px" }}>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}
            >
              City<span style={{ color: "red", marginLeft: 4 }}>*</span>
            </Typography>
            <InputBase
              name="city"
              value={supplierForm.city}
              onChange={handleSupplierInputChange}
              placeholder="Enter city"
              sx={{
                backgroundColor: "white",
                width: "100%",
                height: "40px",
                borderRadius: "4px",
                border: "1px solid",
                borderColor: supplierErrors.city ? "red" : "secondary.border",
                padding: "8px 12px",
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "22px",
                color: "black",
              }}
            />
            {supplierErrors.city && (
              <Typography
                sx={{ color: "red", fontSize: "12px", lineHeight: "18px" }}
              >
                {supplierErrors.city}
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}
            >
              State<span style={{ color: "red", marginLeft: 4 }}>*</span>
            </Typography>
            <InputBase
              name="state"
              value={supplierForm.state}
              onChange={handleSupplierInputChange}
              placeholder="Enter state"
              sx={{
                backgroundColor: "white",
                width: "100%",
                height: "40px",
                borderRadius: "4px",
                border: "1px solid",
                borderColor: supplierErrors.state ? "red" : "secondary.border",
                padding: "8px 12px",
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "22px",
                color: "black",
              }}
            />
            {supplierErrors.state && (
              <Typography
                sx={{ color: "red", fontSize: "12px", lineHeight: "18px" }}
              >
                {supplierErrors.state}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: "16px" }}>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}
            >
              Pincode<span style={{ color: "red", marginLeft: 4 }}>*</span>
            </Typography>
            <InputBase
              name="pincode"
              value={supplierForm.pincode}
              onChange={handleSupplierInputChange}
              placeholder="Enter pincode"
              sx={{
                backgroundColor: "white",
                width: "100%",
                height: "40px",
                borderRadius: "4px",
                border: "1px solid",
                borderColor: supplierErrors.pincode ? "red" : "secondary.border",
                padding: "8px 12px",
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "22px",
                color: "black",
              }}
            />
            {supplierErrors.pincode && (
              <Typography
                sx={{ color: "red", fontSize: "12px", lineHeight: "18px" }}
              >
                {supplierErrors.pincode}
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}
            >
              Country
            </Typography>
            <InputBase
              name="country"
              value={supplierForm.country}
              onChange={handleSupplierInputChange}
              placeholder="Enter country"
              sx={{
                backgroundColor: "white",
                width: "100%",
                height: "40px",
                borderRadius: "4px",
                border: "1px solid",
                borderColor: "secondary.border",
                padding: "8px 12px",
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "22px",
                color: "black",
              }}
            />
          </Box>
        </Box>

        {/* Business & Legal Information */}
        <Typography sx={{ fontSize: "18px", fontWeight: "600", mt: 2 }}>
          Business & Legal Information
        </Typography>

        <Box sx={{ display: "flex", gap: "16px" }}>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}
            >
              GST Number
            </Typography>
            <InputBase
              name="gst_number"
              value={supplierForm.gst_number}
              onChange={handleSupplierInputChange}
              placeholder="Enter GST number"
              sx={{
                backgroundColor: "white",
                width: "100%",
                height: "40px",
                borderRadius: "4px",
                border: "1px solid",
                borderColor: supplierErrors.gst_number ? "red" : "secondary.border",
                padding: "8px 12px",
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "22px",
                color: "black",
              }}
            />
            {supplierErrors.gst_number && (
              <Typography
                sx={{ color: "red", fontSize: "12px", lineHeight: "18px" }}
              >
                {supplierErrors.gst_number}
              </Typography>
            )}
          </Box>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}
            >
              PAN Number
            </Typography>
            <InputBase
              name="pan_number"
              value={supplierForm.pan_number}
              onChange={handleSupplierInputChange}
              placeholder="Enter PAN number"
              sx={{
                backgroundColor: "white",
                width: "100%",
                height: "40px",
                borderRadius: "4px",
                border: "1px solid",
                borderColor: supplierErrors.pan_number ? "red" : "secondary.border",
                padding: "8px 12px",
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "22px",
                color: "black",
              }}
            />
            {supplierErrors.pan_number && (
              <Typography
                sx={{ color: "red", fontSize: "12px", lineHeight: "18px" }}
              >
                {supplierErrors.pan_number}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <Typography
            sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}
          >
            Registration Number
          </Typography>
          <InputBase
            name="registration_number"
            value={supplierForm.registration_number}
            onChange={handleSupplierInputChange}
            placeholder="Enter registration number"
            sx={{
              backgroundColor: "white",
              width: "100%",
              height: "40px",
              borderRadius: "4px",
              border: "1px solid",
              borderColor: "secondary.border",
              padding: "8px 12px",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "22px",
              color: "black",
            }}
          />
        </Box>

        {/* Operational Information */}
        <Typography sx={{ fontSize: "18px", fontWeight: "600", mt: 2 }}>
          Operational Information
        </Typography>

        <Box sx={{ display: "flex", gap: "16px" }}>
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: "8px" }}>
            <Typography
              sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}
            >
              Lead Time (Days)
            </Typography>
            <InputBase
              type="number"
              name="lead_time_days"
              value={supplierForm.lead_time_days}
              onChange={handleSupplierInputChange}
              placeholder="Enter lead time in days"
              sx={{
                backgroundColor: "white",
                width: "100%",
                height: "40px",
                borderRadius: "4px",
                border: "1px solid",
                borderColor: supplierErrors.lead_time_days ? "red" : "secondary.border",
                padding: "8px 12px",
                fontSize: "14px",
                fontWeight: "400",
                lineHeight: "22px",
                color: "black",
              }}
            />
            {supplierErrors.lead_time_days && (
              <Typography
                sx={{ color: "red", fontSize: "12px", lineHeight: "18px" }}
              >
                {supplierErrors.lead_time_days}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <Typography
            sx={{ fontSize: "14px", fontWeight: "400", lineHeight: "22px" }}
          >
            Delivery Terms
          </Typography>
          <InputBase
            name="delivery_terms"
            value={supplierForm.delivery_terms}
            onChange={handleSupplierInputChange}
            placeholder="Enter delivery terms"
            multiline
            rows={2}
            sx={{
              backgroundColor: "white",
              width: "100%",
              minHeight: "60px",
              borderRadius: "4px",
              border: "1px solid",
              borderColor: "secondary.border",
              padding: "8px 12px",
              fontSize: "14px",
              fontWeight: "400",
              lineHeight: "22px",
              color: "black",
            }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: "24px", mt: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Switch
              checked={!!supplierForm.is_active}
              onChange={handleSupplierToggleChange("is_active")}
            />
            <Typography sx={{ fontSize: "14px" }}>Active</Typography>
          </Box>
        </Box>
      </Box>

      {/* Footer Actions */}
      <Box
        sx={{
          backgroundColor: "white",
          padding: "16px 24px",
          borderRadius: "8px",
          display: "flex",
          justifyContent: "flex-end",
          gap: "16px",
        }}
      >
        <Button
          variant="outlined"
          sx={{
            textTransform: "none",
            color: "black",
            backgroundColor: "white",
            minWidth: "100px",
          }}
          onClick={() => navigate("/home/suppliers")}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          sx={{
            textTransform: "none",
            minWidth: "100px",
            position: "relative",
          }}
          onClick={handleSaveSupplier}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: "#ED1E79" }} />
          ) : (
            isEditMode ? "Update" : "Create"
          )}
        </Button>
      </Box>
    </Box>
  );
}

